/* eslint-disable max-lines */
/* eslint-disable react/boolean-prop-naming */
/* eslint-disable react/jsx-handler-names */
"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { useDispatch, useSelector } from "react-redux";
import {
  clearPersonlizedImageStore,
  setMultiImageConfig,
  setMultiImageText,
  setSuccessfullyAddedToast,
  setUserPersonalization,
} from "../../../../store/slices/personalizationSlice";
import PropTypes from "prop-types";
import {
  checkMultiUploadedImageCount,
  checkPersonalizationValidation,
  getValidPersonalizationValues,
  handlePersonalizationDetailsOnRefresh,
  handleSetUserPersonalizations,
  imageCountConfig,
} from "../../../../utils/pdp/personalization";
import ButtonComponent from "../../../atoms/button";
import Drawer from "../../drawer";
import Typography from "../../../atoms/typography";
import Toast from "../../../atoms/toast";
import { TOAST_EMBEDDED_PRECHECKOUT_POSITION } from "../../../atoms/toast/toastAnimationUtils";
import { constants, UPLOAD_PHOTOS } from "../../../../constants/pageConstants/pdpConstants";
import IconButtonComponent from "../../../atoms/iconButton";
import ImageAndTextTab from "../multiImageWithTextPersonalization/imageAndTextTab";
import { unlockBody } from "../../../../utils/document";
import { usePreCheckoutEmbedded } from "../../../../customHooks/usePreCheckoutEmbedded";
import useAnalytics from "../../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import { closeBottomDrawerFromParent } from "../../../../utils/common";

const MultiImageUploadInput = dynamic(() => import("./multiImageUploadInput"));
const FileUploadInstructions = dynamic(() => import("./fileUploadInstructions"));

/**
 * MultiImagePersonalization component for managing image uploads in product personalization.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.item - Contains the configuration details such as allowed media types, size constraints, and field metadata for the image upload.
 * @param {string} props.productId - The unique identifier for the product associated with the personalization.
 * @param {function} props.setPreviewImage - Function to set or update the preview image upon successful image upload.
 * @param {string} props.previewImage - The currently displayed preview image URL.
 * @param {boolean} props.isMobile - Flag indicating if the component is rendered in a mobile view.
 * @param {boolean} props.imageWithText - Flag to indicate if the personalization includes both images and text.
 * @param {Array} props.personalizedOptions - Array of personalization options for the product.
 * @param {function} props.setTextEntered - Function to set the text entered status.
 *
 * @returns {JSX.Element} The rendered MultiImagePersonalization component.
 */
const MultiImagePersonalization = ({
  item,
  productId,
  setPreviewImage,
  previewImage,
  isMobile,
  imageWithText,
  personalizedOptions,
  setTextEntered,
  cleverTapData,
  drawerProps,
}) => {
  const { maxInputRequired, minInputRequired, fieldName, contentType, label } = item;
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [currentToast, setCurrentToast] = useState({});
  const [isToastVisible, setIsToastVisible] = useState(null);
  const [allImageUploadValidation, setAllImageUploadValidation] = useState(false);

  const {
    isMultiImageDrawerOpen,
    onOpenMultiImageDrawer,
    onCloseMultiImageDrawer,
    setIsMultiImageDrawerOpen,
    hideVisibleUI,
    isEmbeddedInPreCheckout,
    onPersonalizationSaved,
  } = drawerProps || {};

  const multiImageConfig = useSelector((state) => state.personalization.multiImageConfig);
  const uploadFinalStatus = useSelector((state) => state.personalization?.uploadFinalStatus);
  const imageUploadError = useSelector((state) => state.personalization?.imageUploadError);
  const uploadProgressStatus = useSelector((state) => state.personalization?.uploadProgressStatus);
  const userPersonalization = useSelector((state) => state.personalization.userPersonalization);
  const multiImageText = useSelector((state) => state.personalization.multiImageText);
  const clearPersonalizedData = useSelector((state) => state.personalization.clearPersonalizedData);

  const uploadedImageCount = checkMultiUploadedImageCount(multiImageConfig);
  const allImgUploaded = parseInt(minInputRequired, 10) === uploadedImageCount;
  const isUploadIncomplete = parseInt(minInputRequired, 10) > uploadedImageCount;
  const isUploadInProgress = uploadProgressStatus?.percentComplete !== "" && !uploadFinalStatus;
  const initialConfig = imageCountConfig(maxInputRequired);

  useEffect(() => {
    if (clearPersonalizedData) {
      dispatch(setMultiImageConfig(initialConfig));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearPersonalizedData]);

  useEffect(() => {
    // Fetch data from local storage on component load
    const personalizationFromLocalStore = handlePersonalizationDetailsOnRefresh(productId);
    const matchingEntry =
      personalizationFromLocalStore &&
      Array.isArray(personalizationFromLocalStore) &&
      personalizationFromLocalStore?.find((entry) => entry.fieldName === fieldName);
    if (matchingEntry && getValidPersonalizationValues(matchingEntry.value).length > 0) {
      imageWithText && setTextEntered(true);
      dispatch(setUserPersonalization(personalizationFromLocalStore));

      if (imageWithText && Array.isArray(personalizationFromLocalStore)) {
        const savedTextEntries = personalizationFromLocalStore.filter((entry) => entry.contentType === constants.TEXT);
        if (savedTextEntries.length > 0) {
          dispatch(setMultiImageText(savedTextEntries));
        }
      }

      // Find the specific entry for the current fieldName and set it as the text field value
      const configsMappedFromLocalStorage = initialConfig?.map((item, index) => {
        const imageUrl = matchingEntry?.value?.[index];
        if (imageUrl) {
          return {
            ...item,
            imgUrl: imageUrl,
            uploaded: true,
          };
        }
        return item;
      });
      dispatch(setMultiImageConfig(configsMappedFromLocalStorage));
    } else {
      dispatch(setMultiImageConfig(initialConfig));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, productId]);

  useEffect(() => {
    if (allImgUploaded) {
      setPreviewImage(true);
      trackEvent(
        cleverTapCommonEvents.uploadPhotoDone,
        {
          ...cleverTapData,
          photo_count: uploadedImageCount,
          is_photo_uploaded: true,
        },
        ["clevertap"],
      );
    } else {
      setPreviewImage(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allImgUploaded]);

  /**
   * Sets validation error and shows toast when required images are not uploaded.
   */
  const handleUploadValidationError = () => {
    setAllImageUploadValidation(true);
    setIsToastVisible(true);
    setCurrentToast({});
  };

  /**
   * Saves personalization data to storage, handling different cases based on image and text configurations.
   */
  const savePersonalizationToStorage = useCallback(() => {
    const personalizationValue = {
      fieldName,
      contentType,
      label,
      index: null,
      value: multiImageConfig?.map((image) => image?.imgUrl) || [],
    };

    const personalizationFromStore = imageWithText ? [...multiImageText] : userPersonalization;

    handleSetUserPersonalizations(productId, personalizationValue, personalizationFromStore, dispatch);
  }, [
    contentType,
    dispatch,
    fieldName,
    imageWithText,
    label,
    multiImageConfig,
    multiImageText,
    productId,
    userPersonalization,
  ]);

  const checkValidationForText = useMemo(() => {
    const textContentType = personalizedOptions?.filter((item) => item.contentType === "TEXT");
    const filteredArray =
      Array.isArray(multiImageText) &&
      multiImageText.length > 0 &&
      multiImageText?.filter((item) => item !== null && item?.value.some((value) => value !== ""));

    return textContentType?.length === filteredArray?.length;
  }, [multiImageText, personalizedOptions]);

  /**
   * Proceeds to the preview step and closes the multi-image drawer.
   */
  const proceedToPreview = () => {
    if (imageWithText) {
      if (checkValidationForText) {
        setTextEntered(true);
        setPreviewImage(true);
        dispatch(setSuccessfullyAddedToast(true));
        savePersonalizationToStorage();
        dispatch(clearPersonlizedImageStore());
        onPersonalizationSaved?.();
        onCloseMultiImageDrawer?.();
      } else {
        const tab = document.getElementById("tab_2");
        if (tab) {
          tab.click(); // triggering the click event to switch to second tab
        }
      }
    } else {
      setPreviewImage(true);
      savePersonalizationToStorage();
      dispatch(clearPersonlizedImageStore());
      onPersonalizationSaved?.();
      onCloseMultiImageDrawer?.();
    }
  };

  /**
   * Handles the continuation of the multi-image upload process.
   */
  const onContinueMultiImageUpload = () => {
    if (isUploadIncomplete) {
      handleUploadValidationError();
      return;
    }

    proceedToPreview();
    unlockBody();
  };

  // Handle toast message updates based on state
  useEffect(() => {
    const toastMessages = {
      validationError: {
        variant: "info",
        header: constants.PLEASE_UPLOAD_ALL_PHOTOS_TO_CONTINUE,
      },
      sizeError: {
        variant: "error",
        header: "Upload failed",
        subHeader: constants.PLEASE_CHECK_THE_INSTRUCTION_AGAIN,
      },
      success: {
        variant: "success",
        header: constants.PHOTOS_UPLOADED_SUCCESSFULLY,
      },
      progress: {
        variant: "info",
        header: constants.UPLOADING,
        subHeader: constants.WINDOW_REFRESH_MESSAGE,
        fill: uploadProgressStatus?.percentComplete,
        strokeWidth: "10",
      },
    };

    let currentToast = null;

    switch (true) {
      case allImageUploadValidation:
        currentToast = toastMessages.validationError;
        break;
      case imageUploadError:
        currentToast = toastMessages.sizeError;
        break;
      case uploadFinalStatus:
        currentToast = toastMessages.success;
        break;
      case !!uploadProgressStatus:
        currentToast = toastMessages.progress;
        break;
      default:
        break;
    }

    if (currentToast) {
      setCurrentToast(currentToast);
    }
  }, [uploadProgressStatus, uploadFinalStatus, allImageUploadValidation, imageUploadError]);

  /**
   * Checks if the toast should be cleared based on the upload or validation status.
   * @returns {boolean} - True if any error or final status is present.
   */
  const shouldClearToast = () => uploadFinalStatus || allImageUploadValidation || imageUploadError;

  /**
   * Resets the toast state by clearing the current toast and hiding it.
   */
  const resetToastState = () => {
    setCurrentToast({});
    setIsToastVisible(false);
  };

  /**
   * Handles the closing of the toast message.
   */
  const handleToastClose = () => {
    if (shouldClearToast()) {
      resetToastState();
      dispatch(clearPersonlizedImageStore());
      setAllImageUploadValidation(false);
    }
  };

  /**
   * Handles the closing of the Drawer.
   */
  const onDrawerAndTapClose = useCallback(() => {
    if (isUploadInProgress) {
      return;
    }

    unlockBody();
    closeBottomDrawerFromParent();

    const savedPersonalization = handlePersonalizationDetailsOnRefresh(productId);
    const personalizationInfo = imageWithText
      ? { personalizedOptions, personalizedProduct: true }
      : { personalizedOptions: [item], personalizedProduct: true };
    const isSavedComplete =
      Array.isArray(savedPersonalization) &&
      savedPersonalization.length > 0 &&
      checkPersonalizationValidation(personalizationInfo, savedPersonalization);
    const isSessionComplete = !isUploadIncomplete && (!imageWithText || checkValidationForText);

    if (isSessionComplete) {
      savePersonalizationToStorage();
      imageWithText && setTextEntered(true);
      onPersonalizationSaved?.();
    } else if (isSavedComplete) {
      dispatch(setUserPersonalization(savedPersonalization));
      if (imageWithText) {
        const savedTextEntries = savedPersonalization.filter((entry) => entry.contentType === constants.TEXT);
        dispatch(setMultiImageText(savedTextEntries));
        setTextEntered(true);
      }
    } else {
      savePersonalizationToStorage();
      const persistedPersonalization = handlePersonalizationDetailsOnRefresh(productId);
      if (Array.isArray(persistedPersonalization) && persistedPersonalization.length > 0) {
        dispatch(setUserPersonalization(persistedPersonalization));
      }
      if (imageWithText) {
        setTextEntered(checkValidationForText);
      }
      onPersonalizationSaved?.();
    }

    dispatch(clearPersonlizedImageStore());

    const tab = document.getElementById("tab_1");
    if (tab) {
      tab.click();
    }
    onCloseMultiImageDrawer?.();
  }, [
    checkValidationForText,
    dispatch,
    imageWithText,
    isUploadIncomplete,
    isUploadInProgress,
    item,
    onCloseMultiImageDrawer,
    personalizedOptions,
    productId,
    savePersonalizationToStorage,
    setTextEntered,
    onPersonalizationSaved,
  ]);

  const onBeforeDrawerClose = useCallback(() => !isUploadInProgress, [isUploadInProgress]);
  const isEmbeddedInParent = usePreCheckoutEmbedded();
  const isInlinePersonalization = isEmbeddedInPreCheckout || isEmbeddedInParent;
  const footerClasses = isInlinePersonalization
    ? "fixed left-0 bottom-0 mt-auto shrink-0 flex w-full flex-col items-center gap-[10px] border-t border-gray-200 bg-white-900 px-[16px] py-[16px]"
    : "fixed bottom-0 flex w-full flex-col items-center gap-[10px] border-t border-gray-200 bg-white-900 px-[20px] py-[16px]";

  const commonTypographyProps = {
    Tag: "p",
    classes: "w-full flex items-center flex-shrink-0 font-600 text-16 text-fnp-500",
  };

  const mobileStyles = "px-[16px] py-[10px] sm:flex border-b border-gray-300 ";
  const desktopStyles = "h-[48px] px-[16px] py-[10px] justify-between border-b border-gray-200 bg-white-900 shadow-sm";

  const drawerBody = (
    <div className={isInlinePersonalization ? "relative flex h-full min-h-0 w-full flex-1 flex-col" : "w-full"}>
      {!isInlinePersonalization &&
        (isMobile ? (
          <div className={`flex items-center justify-start ${mobileStyles}`}>
            <IconButtonComponent
              labelColor="w-full font-normal"
              classes="py-2"
              iconSrc="/icons/left-arrow.svg"
              onClick={onDrawerAndTapClose}
            />
            <Typography {...commonTypographyProps} classes={`${commonTypographyProps.classes} pl-12`}>
              {imageWithText ? constants.CUSTOMIZE_PRODUCT : constants.UPLOAD_PHOTOS}
            </Typography>
          </div>
        ) : (
          <Typography {...commonTypographyProps} classes={`${commonTypographyProps.classes} ${desktopStyles}`}>
            {imageWithText ? constants.CUSTOMIZE_PRODUCT : constants.UPLOAD_PHOTOS}
          </Typography>
        ))}
      {imageWithText ? (
        <ImageAndTextTab
          item={item}
          productId={productId}
          setPreviewImage={setPreviewImage}
          isMobile={isMobile}
          imageWithText={imageWithText}
          personalizedOptions={personalizedOptions}
          setTextEntered={setTextEntered}
          isUploadIncomplete={isUploadIncomplete}
          setIsToastVisible={setIsToastVisible}
          setCurrentToast={setCurrentToast}
          setIsMultiImageDrawerOpen={setIsMultiImageDrawerOpen}
          onContinueMultiImageUpload={onContinueMultiImageUpload}
          drawerProps={drawerProps}
        />
      ) : (
        <>
          <div
            className={
              isInlinePersonalization
                ? "min-h-0 flex-1 overflow-y-auto pb-80"
                : "h-[calc(100vh-130px)] overflow-auto pb-20 pl-20 pr-20 pt-12"
            }
          >
            <MultiImageUploadInput
              item={item}
              productId={productId}
              setIsToastVisible={setIsToastVisible}
              setCurrentToast={setCurrentToast}
              onPersonalizationSaved={drawerProps?.onPersonalizationSaved}
            />
            <FileUploadInstructions item={item} />
          </div>
          <div className={footerClasses}>
            <ButtonComponent
              classes="text-base flex justify-center w-full"
              buttonVariant="filled"
              label="Continue"
              onClick={onContinueMultiImageUpload}
              isRemoveExtraPadding={true}
            />
          </div>
        </>
      )}
      {isToastVisible && (
        <Toast
          variant={currentToast?.variant}
          isToast={isToastVisible}
          header={currentToast?.header}
          subHeader={currentToast?.subHeader}
          duration={3000}
          isDrawer={!isInlinePersonalization}
          isMobile={isMobile}
          placement="bottom"
          position={isInlinePersonalization ? TOAST_EMBEDDED_PRECHECKOUT_POSITION : ""}
          onToastChange={handleToastClose}
          fill={currentToast?.fill}
          strokeWidth={currentToast?.strokeWidth || "10"}
        />
      )}
    </div>
  );

  return (
    <div className={isInlinePersonalization ? "flex h-full min-h-0 flex-1 flex-col" : "w-full"}>
      {!hideVisibleUI && (
        <ButtonComponent
          classes="text-base flex justify-center w-full"
          buttonVariant="customOutlined"
          label={imageWithText || previewImage ? constants.EDIT : UPLOAD_PHOTOS(maxInputRequired)}
          onClick={onOpenMultiImageDrawer}
          isRemoveExtraPadding={true}
        />
      )}
      {(isMultiImageDrawerOpen || isEmbeddedInPreCheckout) && (
        <Drawer
          position={isMobile ? "bottom" : "right"}
          isOpen={isMultiImageDrawerOpen || isEmbeddedInPreCheckout}
          onDrawerClose={onDrawerAndTapClose}
          onBeforeClose={onBeforeDrawerClose}
          styles={`p-0 h-full ${isMobile ? "" : "min-w-[360px] max-w-[400px] xll:max-w-[532px] xl:max-w-[400px]"}`}
          isDrawerCustomStyles={isMobile ? false : true}
          childContainerClass={isInlinePersonalization ? "flex h-full min-h-0 flex-1 flex-col" : ""}
        >
          {drawerBody}
        </Drawer>
      )}
    </div>
  );
};

MultiImagePersonalization.propTypes = {
  item: PropTypes.shape({
    allowedMediaType: PropTypes.string.isRequired,
    minSize: PropTypes.number.isRequired,
    minUnit: PropTypes.string.isRequired,
    maxSize: PropTypes.number.isRequired,
    maxUnit: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    maxInputRequired: PropTypes.number.isRequired,
    minInputRequired: PropTypes.number.isRequired,
    value: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  indexNumber: PropTypes.number,
  setPreviewImage: PropTypes.func.isRequired,
  setImageSizeError: PropTypes.func.isRequired,
  productId: PropTypes.string.isRequired,
  previewImage: PropTypes.bool,
  isMobile: PropTypes.bool,
  imageWithText: PropTypes.bool.isRequired,
  personalizedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  setTextEntered: PropTypes.func.isRequired,
  cleverTapData: PropTypes.object,
  drawerProps: PropTypes.object,
};

export default MultiImagePersonalization;
